import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  Building2, 
  Users, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Activity,
  Filter,
  Search,
  Download,
  RefreshCw,
  Eye,
  Edit,
  Mail,
  Phone,
  X,
  ChevronDown,
  Calendar
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

const AgencyDashboard = () => {
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const [agencies, setAgencies] = useState([])
  const [selectedAgency, setSelectedAgency] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastAssessmentData, setLastAssessmentData] = useState({})
  const [refreshing, setRefreshing] = useState(false)
  const [riskAssignments, setRiskAssignments] = useState([])
  const [lastUpdated, setLastUpdated] = useState('Never')

  // Check if user has admin privileges
  const isAdmin = profile?.user_type === 'Administrator' || 
                 profile?.user_type === 'IT Administrator' || 
                 profile?.user_type === 'Security Officer'

  // Debug logging
  useEffect(() => {
    console.log('🔍 Agency Dashboard Debug:');
    console.log('User:', user);
    console.log('Profile:', profile);
    console.log('User Type:', profile?.user_type);
    console.log('Is Admin:', isAdmin);
  }, [user, profile])

  useEffect(() => {
    if (isAdmin) {
      loadAgencyData()
    }
  }, [isAdmin])

  const loadAgencyData = async () => {
    try {
      setLoading(true)
      
      // Fetch all agencies with their assessment data
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, agency, user_type, created_at')
        .not('agency', 'is', null)

      if (usersError) throw usersError

      // Fetch assessment data
      const { data: assessments, error: assessmentsError } = await supabase
        .from('assessments')
        .select('*, users!inner(name, agency)')
        .not('users.agency', 'is', null)

      if (assessmentsError) throw assessmentsError

      // Fetch agency risk scores from the connected table
      const { data: riskScores, error: riskError } = await supabase
        .from('agency_risk_scores')
        .select('*')

      if (riskError) throw riskError

      // Process agency data
      const agencyMap = new Map()
      
      // Group users by agency
      users.forEach(user => {
        if (!agencyMap.has(user.agency)) {
          agencyMap.set(user.agency, {
            name: user.agency,
            users: [],
            assessments: [],
            riskAssignments: [],
            totalScore: 0,
            assessmentCount: 0,
            lastAssessment: null,
            riskLevel: 'Unknown',
            riskColor: 'gray',
            agencyRiskScore: 0
          })
        }
        
        agencyMap.get(user.agency).users.push(user)
      })

      // Process assessments
      assessments.forEach(assessment => {
        const agency = assessment.users.agency
        if (agencyMap.has(agency)) {
          const agencyData = agencyMap.get(agency)
          agencyData.assessments.push(assessment)
          agencyData.totalScore += assessment.total_score || 0
          agencyData.assessmentCount++
          
          if (!agencyData.lastAssessment || 
              new Date(assessment.created_at) > new Date(agencyData.lastAssessment)) {
            agencyData.lastAssessment = assessment.created_at
          }
        }
      })

      // Process risk assignments from the connected table
      riskScores.forEach(risk => {
        if (agencyMap.has(risk.agency_name)) {
          const agencyData = agencyMap.get(risk.agency_name)
          agencyData.riskAssignments.push(risk)
          // Use the latest risk score from the connected table
          if (risk.agency_risk_score > agencyData.agencyRiskScore) {
            agencyData.agencyRiskScore = risk.agency_risk_score
          }
        }
      })

      // Calculate risk levels and statistics
      const processedAgencies = Array.from(agencyMap.values()).map(agency => {
        const avgScore = agency.assessmentCount > 0 ? agency.totalScore / agency.assessmentCount : 0
        // Use agencyRiskScore from the connected table if available, otherwise calculate from assessments
        const finalScore = agency.agencyRiskScore > 0 ? agency.agencyRiskScore : Math.round(avgScore)
        const riskLevel = getRiskLevel(finalScore)
        
        return {
          ...agency,
          averageScore: finalScore,
          riskLevel: riskLevel.level,
          riskColor: riskLevel.color,
          complianceRate: calculateComplianceRate(agency.assessments),
          userCount: agency.users.length,
          activeUsers: agency.users.filter(u => {
            const lastActive = new Date(u.created_at)
            const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            return lastActive > thirtyDaysAgo
          }).length,
          riskAssignmentCount: agency.riskAssignments.length
        }
      })

      setAgencies(processedAgencies)
      setLastAssessmentData(processAssessmentData(processedAgencies))
      
    } catch (error) {
      console.error('Error loading agency data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRiskLevel = (score) => {
    if (score >= 80) return { level: 'Low Risk', color: 'green' }
    if (score >= 60) return { level: 'Medium Risk', color: 'yellow' }
    if (score >= 40) return { level: 'High Risk', color: 'orange' }
    return { level: 'Critical Risk', color: 'red' }
  }

  const calculateComplianceRate = (assessments) => {
    if (assessments.length === 0) return 0
    const compliantAssessments = assessments.filter(a => (a.total_score || 0) >= 70).length
    return Math.round((compliantAssessments / assessments.length) * 100)
  }

  const processAssessmentData = (agencies) => {
    const totalAgencies = agencies.length
    const lowRisk = agencies.filter(a => a.riskLevel === 'Low Risk').length
    const mediumRisk = agencies.filter(a => a.riskLevel === 'Medium Risk').length
    const highRisk = agencies.filter(a => a.riskLevel === 'High Risk').length
    const criticalRisk = agencies.filter(a => a.riskLevel === 'Critical Risk').length
    
    const totalScore = agencies.reduce((sum, a) => sum + a.averageScore, 0)
    const totalCompliance = agencies.reduce((sum, a) => sum + a.complianceRate, 0)
    
    return {
      totalAgencies,
      lowRisk,
      mediumRisk,
      highRisk,
      criticalRisk,
      averageScore: totalAgencies > 0 ? Math.round(totalScore / totalAgencies) : 0,
      overallCompliance: totalAgencies > 0 ? Math.round(totalCompliance / totalAgencies) : 0
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await loadAgencyData()
    setRefreshing(false)
  }

  // Navigation functions removed as cards are no longer displayed

  // Show loading if profile is not yet loaded
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user profile...</p>
        </div>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Agency Dashboard.</p>
          <p className="text-sm text-gray-500 mt-2">This feature is only available to administrators.</p>
          <p className="text-xs text-gray-400 mt-2">Your user type: {profile?.user_type || 'Not loaded'}</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading agency data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Building2 className="h-8 w-8 text-blue-600" />
                Agency Dashboard
              </h1>
              <p className="text-gray-600 mt-2">Monitor and manage agency security compliance across your organization</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>

            </div>
          </div>
        </div>

        {/* Admin Dashboard Content - Placeholder */}
        <div className="mb-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-700">Admin Dashboard</h2>
            <p className="text-gray-500 mt-2">Dashboard content has been simplified for admin view</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AgencyDashboard